import React, { useState } from 'react';
import { saveLead } from '../services/leadService';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, 
  Phone, 
  Mail, 
  Calendar, 
  Clock, 
  CheckCircle2, 
  Headphones, 
  BookOpen, 
  PenTool, 
  Mic,
  Loader2,
  FileText,
  AlertCircle
} from 'lucide-react';
const BookIELTSMockTest = () => {

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    preferredDate: '',
    preferredTime: '',
    type: 'ielts_mock_test',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await saveLead({
        ...formData,
        source: 'book_ielts_mock_test',
        timestamp: new Date().toISOString(),
      });
      setIsSuccess(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        preferredDate: '',
        preferredTime: '',
        type: 'ielts_mock_test'
      });
    } catch (error) {
      console.error('Error saving lead:', error);
      alert('Thank you for your booking! We will contact you soon to confirm.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      
      
      {/* =========================================
          HERO HEADER
      ========================================= */}
      <section className="relative pt-32 pb-12 bg-slate-900 text-white overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-600/10 rounded-full blur-[100px] pointer-events-none -translate-y-1/2 translate-x-1/2"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block py-1 px-3 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold uppercase tracking-widest mb-4">
              Exam Simulation Center
            </span>
            <h1 className="text-3xl md:text-5xl font-serif font-bold mb-4">
              Test your skills before the <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
                Real Exam Day.
              </span>
            </h1>
            <p className="text-slate-400 text-lg">
              Get an accurate predicted band score with our Cambridge-certified evaluation system.
            </p>
          </motion.div>
        </div>
      </section>

      {/* =========================================
          MAIN CONTENT SPLIT
      ========================================= */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-12 items-start">
            
            {/* LEFT COLUMN: The Value Proposition */}
            <div className="w-full lg:w-1/2 space-y-10">
              
              {/* Feature 1: The Score Report */}
              <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-red-100 transition-colors">
                 <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <FileText size={100} className="text-slate-900" />
                 </div>
                 <h3 className="text-xl font-serif font-bold text-slate-900 mb-2">Detailed Performance Report</h3>
                 <p className="text-slate-600 mb-6 text-sm leading-relaxed">
                   We don't just give you a score. You get a breakdown of your weak areas (e.g., "Lexical Resource" or "Grammatical Range").
                 </p>
                 
                 
                 
                 <div className="flex gap-2 mt-4 text-xs font-bold text-slate-400 uppercase tracking-wider">
                    <span>• 24hr Result</span>
                    <span>• Band 1-9 Scale</span>
                 </div>
              </div>

              {/* Feature 2: The Modules */}
              <div>
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">What's included?</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { title: "Listening", icon: Headphones, desc: "30 Mins • 40 Qs" },
                    { title: "Reading", icon: BookOpen, desc: "60 Mins • 3 Passages" },
                    { title: "Writing", icon: PenTool, desc: "60 Mins • 2 Tasks" },
                    { title: "Speaking", icon: Mic, desc: "15 Mins • Face-to-Face" },
                  ].map((mod, i) => (
                    <div key={i} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                      <div className="w-10 h-10 bg-slate-50 rounded-lg flex items-center justify-center text-red-600 shrink-0">
                        <mod.icon size={20} />
                      </div>
                      <div>
                        <div className="font-bold text-slate-900 text-sm">{mod.title}</div>
                        <div className="text-[10px] text-slate-500 font-medium">{mod.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Warning/Note */}
              <div className="flex gap-4 p-4 bg-amber-50 rounded-xl border border-amber-100 text-amber-800 text-sm">
                <AlertCircle className="shrink-0" size={20} />
                <p>
                  <strong>Note:</strong> Slots are limited to 10 students per batch to ensure accurate Speaking Test simulation.
                </p>
              </div>

            </div>

            {/* RIGHT COLUMN: The Booking Form */}
            <div className="w-full lg:w-1/2">
              <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-6 md:p-10 sticky top-32">
                
                {isSuccess ? (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-10"
                  >
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 size={40} className="text-green-600" />
                    </div>
                    <h2 className="text-3xl font-serif font-bold text-slate-900 mb-2">Slot Confirmed!</h2>
                    <p className="text-slate-600 mb-8">
                      Your request has been queued. Our exam coordinator will call you shortly to finalize the timing.
                    </p>
                    <Link
                      to="/courses"
                      className="inline-block w-full py-4 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-colors"
                    >
                      Browse Study Materials
                    </Link>
                  </motion.div>
                ) : (
                  <>
                    <h2 className="text-2xl font-serif font-bold text-slate-900 mb-6">Reserve your seat</h2>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                      
                      {/* Name */}
                      <div className="relative group">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-red-600 transition-colors" size={18} />
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Candidate Name"
                          className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium"
                        />
                      </div>

                      {/* Phone */}
                      <div className="relative group">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-red-600 transition-colors" size={18} />
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Phone Number"
                          className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium"
                        />
                      </div>

                      {/* Email */}
                      <div className="relative group">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-red-600 transition-colors" size={18} />
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Email Address"
                          className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium"
                        />
                      </div>

                      {/* Date & Time Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="relative group">
                          <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-red-600 transition-colors" size={18} />
                          <input
                            type="date"
                            name="preferredDate"
                            required
                            value={formData.preferredDate}
                            onChange={handleChange}
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none transition-all text-slate-500 font-medium text-sm"
                          />
                        </div>

                        <div className="relative group">
                          <Clock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-red-600 transition-colors" size={18} />
                          <select
                            name="preferredTime"
                            required
                            value={formData.preferredTime}
                            onChange={handleChange}
                            className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none transition-all text-slate-500 font-medium text-sm appearance-none"
                          >
                            <option value="">Time Slot</option>
                            <option value="morning">Morning (9-12)</option>
                            <option value="afternoon">Afternoon (1-4)</option>
                            <option value="evening">Evening (4-7)</option>
                          </select>
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-red-600/20 transition-all hover:scale-[1.02] flex items-center justify-center gap-2 mt-4"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="animate-spin" size={20} /> Booking...
                          </>
                        ) : (
                          "Confirm Booking"
                        )}
                      </button>

                    </form>
                  </>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default BookIELTSMockTest;